import { useState, useEffect } from "react";
import { Modal, Form, Button } from "react-bootstrap";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { InputText } from "@2solve/tosolve-inputs";
import handleInputChange from "../../../hooks/inputChange";

function FeaturesModal({
  show,
  onHide,
  isEdit,
  editingFeature,
  onDataAdded,
  onDataEdited,
  postData,
  updateData,
  error,
  setError,
}) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: null,
    slug: null,
    description: null,
  });
  const [initialData, setInitialData] = useState({});

  useEffect(() => {
    if (isEdit && editingFeature) {
      const fd = {
        name: editingFeature.name || null,
        slug: editingFeature.slug || null,
        description: editingFeature.description || null,
      };
      setFormData(fd);
      setInitialData(fd);
    } else {
      setFormData({ name: null, slug: null, description: null });
      setInitialData({});
    }
  }, [isEdit, editingFeature, show]);

  const closeModal = () => {
    setFormData({ name: null, slug: null, description: null });
    setError("");
    onHide();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.name.trim()) {
      setError(t("O campo Nome é obrigatório."));
      return;
    }
    if (!formData.slug || !formData.slug.trim()) {
      setError(t("O campo Slug é obrigatório."));
      return;
    }

    if (!isEdit) {
      try {
        await postData("/features", {
          name: formData.name.trim(),
          slug: formData.slug.trim(),
          description: formData.description ? formData.description : null,
          is_enabled: true,
        });
        onDataAdded();
        closeModal();
      } catch (err) {
        console.error(err);
        setError(t("Erro ao cadastrar feature"));
      }
    } else {
      const modified = {};
      const nameTrim = formData.name ? formData.name.trim() : null;
      const slugTrim = formData.slug ? formData.slug.trim() : null;
      const descTrim = formData.description
        ? formData.description.trim()
        : null;

      if (nameTrim !== (initialData.name || null)) modified.name = nameTrim;
      if (slugTrim !== (initialData.slug || null)) modified.slug = slugTrim;
      if (descTrim !== (initialData.description || null))
        modified.description = descTrim;

      if (Object.keys(modified).length === 0) {
        setError(t("Nenhuma alteração foi feita"));
        return;
      }

      try {
        await updateData(`/features/${editingFeature.id}`, modified);
        onDataEdited();
        closeModal();
      } catch (err) {
        console.error(err);
        setError(t("Erro ao editar feature"));
      }
    }
  };

  return (
    <Modal backdrop="static" size="lg" centered show={show} onHide={closeModal}>
      <Modal.Header closeButton closeVariant="white">
        <Modal.Title>
          {isEdit ? t("Editar Feature") : t("Cadastrar Feature")}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <InputText
            id="name"
            type="text"
            placeholder={t("Nome")}
            required={true}
            maxLength={40}
            value={formData.name || ""}
            onChange={handleInputChange(setFormData)}
          />

          <InputText
            className="mt-3"
            id="slug"
            type="text"
            placeholder={t("Slug")}
            required={true}
            maxLength={80}
            value={formData.slug || ""}
            onChange={handleInputChange(setFormData)}
          />
          <InputText
            className="mt-3"
            id="description"
            type="text"
            placeholder={t("Descrição")}
            required={true}
            maxLength={80}
            value={formData.description || ""}
            onChange={handleInputChange(setFormData)}
          />

          {error && <div className="text-danger">{error}</div>}
        </Modal.Body>
        <Modal.Footer>
          <Button type="submit" variant="primary">
            {isEdit ? t("Salvar") : t("Cadastrar")}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

FeaturesModal.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func,
  isEdit: PropTypes.bool,
  editingFeature: PropTypes.object,
  onDataAdded: PropTypes.func,
  onDataEdited: PropTypes.func,
  postData: PropTypes.func,
  updateData: PropTypes.func,
  error: PropTypes.string,
  setError: PropTypes.func,
};

export default FeaturesModal;
